export interface SrgbColor {
  r: number;
  g: number;
  b: number;
}

export const APCA_MIN_LC = {
  bodyText: 75,
  contentText: 60,
  largeText: 45,
  spotText: 30,
} as const;

function apcaLuminance({ r, g, b }: SrgbColor): number {
  const [red, green, blue] = [r, g, b].map((channel) => (channel / 255) ** 2.4);
  return red * 0.2126729 + green * 0.7151522 + blue * 0.072175;
}

/** APCA 0.0.98G-4g contrast; sign identifies dark-on-light vs light-on-dark polarity. */
export function getApcaContrast(
  foreground: SrgbColor,
  background: SrgbColor,
): number {
  const softClamp = (luminance: number) =>
    luminance < 0.022 ? luminance + (0.022 - luminance) ** 1.414 : luminance;
  const foregroundLuminance = softClamp(apcaLuminance(foreground));
  const backgroundLuminance = softClamp(apcaLuminance(background));

  if (Math.abs(backgroundLuminance - foregroundLuminance) < 0.0005) return 0;

  if (backgroundLuminance > foregroundLuminance) {
    const contrast =
      (backgroundLuminance ** 0.56 - foregroundLuminance ** 0.57) * 1.14;
    return contrast < 0.1 ? 0 : (contrast - 0.027) * 100;
  }

  const contrast = (backgroundLuminance ** 0.65 - foregroundLuminance ** 0.62) *
    1.14;
  return contrast > -0.1 ? 0 : (contrast + 0.027) * 100;
}

export function meetsApcaContrast(
  foreground: SrgbColor,
  background: SrgbColor,
  minimumLc: number,
): boolean {
  return Math.abs(getApcaContrast(foreground, background)) >= minimumLc;
}
